import React, { useState } from 'react';
import { SearchIcon, FilterIcon, BookmarkIcon, ThumbsUpIcon, UserIcon, XIcon, CheckIcon, PlusIcon } from 'lucide-react';
interface ResourceHubProps {
  userType: 'mentor' | 'mentee' | null;
}
interface Resource {
  id: number;
  title: string;
  type: 'article' | 'video' | 'audio' | 'prompt';
  categories: string[];
  creator: string;
  creatorImage: string;
  reviewers: string[];
  summary: string;
  saved: boolean;
  likes: number;
  liked: boolean;
  image?: string;
}
const ResourceHub: React.FC<ResourceHubProps> = ({
  userType
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState<Resource | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const resources: Resource[] = [{
    id: 1,
    title: 'Navigating Career Transitions in Tech',
    type: 'article',
    categories: ['Career', 'Growth'],
    creator: 'Alex Morgan',
    creatorImage: 'https://randomuser.me/api/portraits/women/44.jpg',
    reviewers: ['Jamie Rivera', 'Sam Taylor'],
    summary: 'A comprehensive guide to changing careers within the tech industry, with specific advice for LGBTQ+ professionals facing unique challenges.',
    saved: true,
    likes: 24,
    liked: true,
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'
  }, {
    id: 2,
    title: 'Mindfulness Practices for Daily Wellness',
    type: 'audio',
    categories: ['Wellness', 'Growth'],
    creator: 'Jamie Rivera',
    creatorImage: 'https://randomuser.me/api/portraits/nonbinary/9.jpg',
    reviewers: ['Alex Morgan'],
    summary: 'A 10-minute guided meditation specifically designed for managing workplace stress and anxiety.',
    saved: false,
    likes: 18,
    liked: false
  }, {
    id: 3,
    title: 'Building Inclusive Communities',
    type: 'video',
    categories: ['Community', 'Identity'],
    creator: 'Sam Taylor',
    creatorImage: 'https://randomuser.me/api/portraits/men/32.jpg',
    reviewers: ['Alex Morgan', 'Jamie Rivera'],
    summary: 'Learn strategies for creating and maintaining spaces where all members of the LGBTQ+ community feel welcome and valued.',
    saved: true,
    likes: 32,
    liked: true,
    image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1632&q=80'
  }, {
    id: 4,
    title: 'Financial Planning for Queer Couples',
    type: 'article',
    categories: ['Finance', 'Life'],
    creator: 'Alex Morgan',
    creatorImage: 'https://randomuser.me/api/portraits/women/44.jpg',
    reviewers: ['Sam Taylor'],
    summary: 'A guide to navigating financial planning, including legal considerations, joint assets, and retirement planning for LGBTQ+ couples.',
    saved: false,
    likes: 15,
    liked: false,
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80'
  }];
  const filteredResources = resources.filter(resource => resource.title.toLowerCase().includes(searchQuery.toLowerCase()) || resource.categories.some(category => category.toLowerCase().includes(searchQuery.toLowerCase())));
  const toggleSaved = (id: number) => {
    console.log('Toggle saved for resource:', id);
  };
  const toggleLiked = (id: number) => {
    console.log('Toggle liked for resource:', id);
  };
  const ResourceCard = ({
    resource
  }: {
    resource: Resource;
  }) => {
    return <div className="bg-white rounded-xl shadow-sm overflow-hidden" onClick={() => setShowResourceModal(resource)} data-id="element-383">
        {resource.image && <div className="h-32 overflow-hidden" data-id="element-384">
            <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" data-id="element-385" />
          </div>}
        <div className="p-3" data-id="element-386">
          <div className="flex justify-between items-start mb-2" data-id="element-387">
            <h3 className="font-medium" data-id="element-388">{resource.title}</h3>
            <button className="p-1" onClick={e => {
            e.stopPropagation();
            toggleSaved(resource.id);
          }} data-id="element-389">
              <BookmarkIcon size={18} className={resource.saved ? 'text-purple-600 fill-purple-600' : 'text-gray-400'} data-id="element-390" />
            </button>
          </div>
          <div className="flex items-center mb-2" data-id="element-391">
            <div className="w-6 h-6 rounded-full overflow-hidden mr-2" data-id="element-392">
              <img src={resource.creatorImage} alt={resource.creator} className="w-full h-full object-cover" data-id="element-393" />
            </div>
            <span className="text-xs text-gray-600" data-id="element-394">{resource.creator}</span>
          </div>
          <div className="flex flex-wrap mb-2" data-id="element-395">
            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-396">
              {resource.type}
            </span>
            {resource.categories.map(category => <span key={category} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-397">
                {category}
              </span>)}
          </div>
          <p className="text-xs text-gray-600 line-clamp-2 mb-2" data-id="element-398">
            {resource.summary}
          </p>
          <div className="flex justify-between items-center" data-id="element-399">
            <button className="flex items-center text-xs text-gray-500" onClick={e => {
            e.stopPropagation();
            toggleLiked(resource.id);
          }} data-id="element-400">
              <ThumbsUpIcon size={14} className={`mr-1 ${resource.liked ? 'text-purple-600 fill-purple-600' : ''}`} data-id="element-401" />
              {resource.likes}
            </button>
            <div className="flex items-center" data-id="element-402">
              <span className="text-xs text-gray-500 mr-1" data-id="element-403">Reviewed by</span>
              <div className="flex -space-x-2" data-id="element-404">
                {resource.reviewers.map((reviewer, index) => <div key={index} className="w-5 h-5 rounded-full border border-white bg-gray-200 flex items-center justify-center" data-id="element-405">
                    <UserIcon size={12} className="text-gray-500" data-id="element-406" />
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const ResourceModal = ({
    resource
  }: {
    resource: Resource;
  }) => {
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-407">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-id="element-408">
          <div className="relative" data-id="element-409">
            {resource.image ? <div className="h-48 overflow-hidden" data-id="element-410">
                <img src={resource.image} alt={resource.title} className="w-full h-full object-cover" data-id="element-411" />
              </div> : <div className="h-24 bg-purple-100" data-id="element-412"></div>}
            <button className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md" onClick={() => setShowResourceModal(null)} data-id="element-413">
              <XIcon size={20} data-id="element-414" />
            </button>
          </div>
          <div className="p-4" data-id="element-415">
            <div className="flex justify-between items-start mb-3" data-id="element-416">
              <h2 className="text-xl font-semibold" data-id="element-417">{resource.title}</h2>
              <button onClick={() => toggleSaved(resource.id)} data-id="element-418">
                <BookmarkIcon size={20} className={resource.saved ? 'text-purple-600 fill-purple-600' : 'text-gray-400'} data-id="element-419" />
              </button>
            </div>
            <div className="flex items-center mb-4" data-id="element-420">
              <div className="w-8 h-8 rounded-full overflow-hidden mr-2" data-id="element-421">
                <img src={resource.creatorImage} alt={resource.creator} className="w-full h-full object-cover" data-id="element-422" />
              </div>
              <div data-id="element-423">
                <div className="text-sm font-medium" data-id="element-424">{resource.creator}</div>
                <div className="text-xs text-gray-500" data-id="element-425">Creator</div>
              </div>
            </div>
            <div className="flex flex-wrap mb-4" data-id="element-426">
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-427">
                {resource.type}
              </span>
              {resource.categories.map(category => <span key={category} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full mr-1 mb-1" data-id="element-428">
                  {category}
                </span>)}
            </div>
            <div className="mb-4" data-id="element-429">
              <h3 className="text-sm font-medium mb-2" data-id="element-430">Summary</h3>
              <p className="text-sm text-gray-700" data-id="element-431">{resource.summary}</p>
            </div>
            <div className="mb-4" data-id="element-432">
              <h3 className="text-sm font-medium mb-2" data-id="element-433">Peer Reviewed By</h3>
              <div className="flex items-center" data-id="element-434">
                {resource.reviewers.map((reviewer, index) => <div key={index} className="flex items-center mr-3" data-id="element-435">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-1" data-id="element-436">
                      <UserIcon size={12} className="text-gray-500" data-id="element-437" />
                    </div>
                    <span className="text-xs" data-id="element-438">{reviewer}</span>
                  </div>)}
              </div>
            </div>
            <div className="flex justify-between items-center" data-id="element-439">
              <button className="flex items-center text-gray-700" onClick={() => toggleLiked(resource.id)} data-id="element-440">
                <ThumbsUpIcon size={18} className={`mr-1 ${resource.liked ? 'text-purple-600 fill-purple-600' : ''}`} data-id="element-441" />
                {resource.likes} Likes
              </button>
              <button className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm" data-id="element-442">
                View Full Resource
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const FilterModal = () => {
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-443">
        <div className="bg-white rounded-xl w-full max-w-sm" data-id="element-444">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-445">
            <h3 className="font-semibold" data-id="element-446">Filter Resources</h3>
            <button onClick={() => setShowFilters(false)} data-id="element-447">
              <XIcon size={20} data-id="element-448" />
            </button>
          </div>
          <div className="p-4" data-id="element-449">
            <div className="mb-4" data-id="element-450">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-451">
                Resource Type
              </label>
              <div className="space-y-2" data-id="element-452">
                {['All Types', 'Article', 'Video', 'Audio', 'Prompt'].map(type => <label key={type} className="flex items-center" data-id="element-453">
                      <input type="radio" name="type" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300" defaultChecked={type === 'All Types'} data-id="element-454" />
                      <span className="ml-2 text-sm text-gray-700" data-id="element-455">{type}</span>
                    </label>)}
              </div>
            </div>
            <div className="mb-4" data-id="element-456">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-457">
                Categories
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-458">
                {['Career', 'Academic', 'Creative', 'Wellness', 'Growth', 'Finance', 'Identity', 'Community'].map(category => <label key={category} className="flex items-center" data-id="element-459">
                    <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-460" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-461">
                      {category}
                    </span>
                  </label>)}
              </div>
            </div>
            <div className="mb-4" data-id="element-462">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-463">
                Other Filters
              </label>
              <div className="space-y-2" data-id="element-464">
                <label className="flex items-center" data-id="element-465">
                  <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-466" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-467">
                    Saved items only
                  </span>
                </label>
                <label className="flex items-center" data-id="element-468">
                  <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-469" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-470">
                    Peer-reviewed only
                  </span>
                </label>
                <label className="flex items-center" data-id="element-471">
                  <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-472" />
                  <span className="ml-2 text-sm text-gray-700" data-id="element-473">
                    Most popular
                  </span>
                </label>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-474">
              <button className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 mr-2" onClick={() => setShowFilters(false)} data-id="element-475">
                Reset
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg" onClick={() => setShowFilters(false)} data-id="element-476">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  const CreateResourceModal = () => {
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-477">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto" data-id="element-478">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-479">
            <h3 className="font-semibold" data-id="element-480">Create New Resource</h3>
            <button onClick={() => setShowCreateModal(false)} data-id="element-481">
              <XIcon size={20} data-id="element-482" />
            </button>
          </div>
          <div className="p-4" data-id="element-483">
            <div className="mb-4" data-id="element-484">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-485">
                Resource Title
              </label>
              <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Enter a descriptive title" data-id="element-486" />
            </div>
            <div className="mb-4" data-id="element-487">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-488">
                Resource Type
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-489">
                <option value="" data-id="element-490">Select a type</option>
                <option value="article" data-id="element-491">Article</option>
                <option value="video" data-id="element-492">Video</option>
                <option value="audio" data-id="element-493">Audio</option>
                <option value="prompt" data-id="element-494">Prompt</option>
              </select>
            </div>
            <div className="mb-4" data-id="element-495">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-496">
                Categories (Select up to 3)
              </label>
              <div className="grid grid-cols-2 gap-2" data-id="element-497">
                {['Career', 'Academic', 'Creative', 'Wellness', 'Growth', 'Finance', 'Identity', 'Community', 'Focus', 'Goals', 'Life', 'Parenting'].map(category => <label key={category} className="flex items-center" data-id="element-498">
                    <input type="checkbox" className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded" data-id="element-499" />
                    <span className="ml-2 text-sm text-gray-700" data-id="element-500">
                      {category}
                    </span>
                  </label>)}
              </div>
            </div>
            <div className="mb-4" data-id="element-501">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-502">
                Summary
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" rows={3} placeholder="Write a brief summary of this resource" data-id="element-503"></textarea>
            </div>
            <div className="mb-4" data-id="element-504">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-505">
                Content
              </label>
              <textarea className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" rows={6} placeholder="Enter the main content here" data-id="element-506"></textarea>
              <div className="mt-1 text-sm text-purple-600" data-id="element-507">
                AI assistance available
              </div>
            </div>
            <div className="mb-4" data-id="element-508">
              <label className="block text-gray-700 text-sm font-medium mb-1" data-id="element-509">
                Upload Image (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center" data-id="element-510">
                <p className="text-sm text-gray-500" data-id="element-511">
                  Click to upload or drag and drop
                </p>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-512">
              <button className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 mr-2" onClick={() => setShowCreateModal(false)} data-id="element-513">
                Cancel
              </button>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg" onClick={() => setShowCreateModal(false)} data-id="element-514">
                Submit for Review
              </button>
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="pb-4" data-id="element-515">
      <div className="flex justify-between items-center mb-6" data-id="element-516">
        <h1 className="text-xl font-bold" data-id="element-517">Resource Hub</h1>
        <div className="flex space-x-2" data-id="element-518">
          <button className="p-2 bg-gray-100 text-gray-600 rounded-full" onClick={() => setShowFilters(true)} data-id="element-519">
            <FilterIcon size={20} data-id="element-520" />
          </button>
          {userType === 'mentor' && <button className="p-2 bg-purple-600 text-white rounded-full" onClick={() => setShowCreateModal(true)} data-id="element-521">
              <PlusIcon size={20} data-id="element-522" />
            </button>}
        </div>
      </div>
      <div className="mb-4" data-id="element-523">
        <div className="relative" data-id="element-524">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-525">
            <SearchIcon size={18} className="text-gray-400" data-id="element-526" />
          </div>
          <input type="text" className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" placeholder="Search resources..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-527" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-528">
        {filteredResources.map(resource => <ResourceCard key={resource.id} resource={resource} data-id="element-529" />)}
        {filteredResources.length === 0 && <div className="col-span-full p-4 text-center text-gray-500" data-id="element-530">
            No resources found
          </div>}
      </div>
      {showResourceModal && <ResourceModal resource={showResourceModal} data-id="element-531" />}
      {showFilters && <FilterModal data-id="element-532" />}
      {showCreateModal && <CreateResourceModal data-id="element-533" />}
    </div>;
};
export default ResourceHub;